import { RefObject } from 'react';
import { useIntl } from 'react-intl';
import {
  ProductAmount,
  ProductData,
  ProductImageProps,
  ProductOptions,
  QualityMarkProductData,
} from 'types/ProductData';
import { Button, DescriptionWithShowMore, Icon, Tag, Tooltip } from 'ui';
import { ProductCardPrice } from 'ui/Card/ProductCardPrice';
import { FallbackImage } from 'ui/UploadImage/fallBack/FallBackImage';
import { ProductGallery } from 'ui/Carousel/ProductGallery';
import AmountSelector from 'ui/CartAmountSelector/CartAmountSelector';
import { ProductPackageUnit } from 'utils/productCardHelpers';
import { useBreakpoint } from 'utils/useBreakpoint';
import { publicPath } from 'enums/publicRoutes';
import { options } from 'config/config';
import { qualityMarkLogoClassName } from 'utils/qualityMarkHelpers';
import WholesaleInfo from '../WholesaleInfo';
import { ProducerPill } from './ProducerPill';
import CompanyType from 'enums/company-type';

const DEFAULT_IMAGE = options.images.defaultProductImage;

interface ProductHeroProps {
  product?: ProductData;
  productAmount?: ProductAmount;
  productImages?: ProductImageProps[];
  qualityMarks?: QualityMarkProductData[];
  isEmarket: boolean;
  amountCart: number;
  isAddToCartAllowed: boolean;
  addToCartRef: RefObject<HTMLDivElement>;
  onAddToCart: () => void;
  onZoom: () => void;
}

const ProductHero = ({
  product,
  productAmount,
  productImages,
  qualityMarks,
  isEmarket,
  amountCart,
  isAddToCartAllowed,
  addToCartRef,
  onAddToCart,
  onZoom,
}: ProductHeroProps) => {
  const intl = useIntl();
  const { isXsDown, isXsUp } = useBreakpoint();

  const hasGallery = !!productImages?.length;

  const farm = product?.farm;
  const farmName = farm?.brand ?? farm?.name;
  const farmPath = farm?.id
    ? isEmarket
      ? publicPath.farm(farm.slug, farm.id)
      : publicPath.wholesaleFarm(farm.slug, farm.id)
    : undefined;

  const producerName = product?.producer_name;
  const producerFarm = product?.producer_farm;
  const producerPath = producerFarm?.id
    ? isEmarket
      ? publicPath.farm(producerFarm.slug, producerFarm.id)
      : publicPath.wholesaleFarm(producerFarm.slug, producerFarm.id)
    : undefined;

  const producerDisplayName = producerFarm?.brand || producerName;
  const producerPillName = producerDisplayName || farmName;
  const producerPillPath = producerName ? producerPath : farmPath;
  const showDistributor = !!producerName && !!farmName;

  const isCooperative = (entity?: { company_type?: string | null } | null) =>
    entity?.company_type === CompanyType.COOPERATIVE_SOCIETY_KB;
  const producerPillCooperative = producerName
    ? isCooperative(producerFarm)
    : isCooperative(farm);
  const distributorCooperative = isCooperative(farm);

  return (
    <div className={'product-hero'}>
      <div className={'product-hero-media'}>
        {isXsDown && hasGallery ? (
          <ProductGallery productImages={productImages} showDots={false} />
        ) : (
          <FallbackImage
            className={'product-hero-image'}
            src={product?.image_small || product?.farm?.logo || DEFAULT_IMAGE}
            type={'ProductCard'}
            preview={false}
            alt={product?.name}
          />
        )}
        {isXsUp && hasGallery && (
          <button
            type={'button'}
            className={'product-hero-zoom'}
            onClick={onZoom}
          >
            <Icon name={'open-image'} />
          </button>
        )}
      </div>

      <div className={'product-hero-info'}>
        <div className={'product-hero-heading'}>
          <div className={'product-hero-title-row'}>
            <h1 className={'product-hero-title'}>
              {product?.name}{' '}
              <ProductPackageUnit productAmount={productAmount} />
            </h1>
            {!!qualityMarks?.length && (
              <div className={'product-hero-qmarks'}>
                {qualityMarks.map((qmark) => (
                  <Tooltip key={qmark.id} title={qmark.quality_mark?.name}>
                    <span
                      className={`product-hero-qmark ${qualityMarkLogoClassName(
                        qmark.quality_mark?.slug
                      )}`}
                    >
                      <FallbackImage
                        className={'product-hero-qmark-image'}
                        src={qmark.quality_mark?.logo || ''}
                        type={'notFound'}
                        preview={false}
                        alt={qmark.quality_mark?.name}
                      />
                    </span>
                  </Tooltip>
                ))}
              </div>
            )}
          </div>

          {!!producerPillName && (
            <div className={'product-hero-producers'}>
              <ProducerPill
                label={'product_view.producer_with_name'}
                name={producerPillName}
                to={producerPillPath}
                icon={'location-pin-icon'}
                cooperative={producerPillCooperative}
              />
              {showDistributor && (
                <ProducerPill
                  label={'product_view.distributor_with_name'}
                  name={farmName}
                  to={farmPath}
                  icon={'distributor-icon'}
                  cooperative={distributorCooperative}
                />
              )}
            </div>
          )}
        </div>

        {!!product?.description && (
          <DescriptionWithShowMore showMore text={product.description} />
        )}

        {!!product?.options?.length && (
          <div className={'product-hero-options'}>
            {product.options.map((option: ProductOptions) => (
              <Tag
                key={option.id}
                className={'product-option-tag'}
                bordered={true}
              >
                {option.label}
              </Tag>
            ))}
          </div>
        )}

        <div className={'product-hero-price'}>
          <ProductCardPrice productAmount={productAmount} amount={amountCart} />
          <WholesaleInfo productAmount={productAmount} />
        </div>

        {isAddToCartAllowed && (
          <div className={'product-hero-cart'} ref={addToCartRef}>
            {amountCart === 0 ? (
              <Button
                type={'primary'}
                className={'product-hero-add-btn'}
                label={intl.formatMessage({ id: 'button.addToCartDesktop' })}
                onClick={onAddToCart}
              />
            ) : (
              <AmountSelector
                product={product}
                unitType={productAmount?.storage_unit}
                amountCart={amountCart}
                className={'productView'}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductHero;
